import React, { ChangeEvent, SyntheticEvent, useState } from 'react';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import {
  Button,
  Checkbox,
  FilterOptionsState,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import { PartialFinanceOperatorsItem } from 'entities/financeOperations/types';
import { CitiesListType } from 'features/getCities/types';
import { Controller, SubmitHandler, UseFormReturn } from 'react-hook-form';

type FinanceOperationsFormPropsType = {
  citiesList: CitiesListType[];
  citiesDefault: CitiesListType[];
  form: UseFormReturn<PartialFinanceOperatorsItem>;
  onSubmit: (data: PartialFinanceOperatorsItem) => SubmitHandler<PartialFinanceOperatorsItem>;
};
export const FinanceOperationsForm = ({
  citiesList,
  citiesDefault,
  form,
  onSubmit,
}: FinanceOperationsFormPropsType) => {
  const [selectValues, setSelectValues] = useState(citiesDefault);
  const { control, setValue, handleSubmit } = form;

  const handleAutocompleteChange = (event: SyntheticEvent, values: CitiesListType[]) => {
    if (values.find((value) => value.name_ru === 'Выбрать все')) {
      const citiesIds: number[] = citiesList.map((city) => city.id);
      setValue('city_ids', citiesIds);
      return setSelectValues(values.length - 1 === citiesList.length ? [] : citiesList);
    }
    const citiesIds: number[] = values.map((value) => value.id);
    setValue('city_ids', citiesIds);
    setSelectValues(values);
  };

  const filterOptions = (options: CitiesListType[], params: FilterOptionsState<CitiesListType>) => {
    const filter = createFilterOptions<CitiesListType>();
    const filtered = filter(options, params);
    return [{ name_ru: 'Выбрать все' }, ...filtered] as CitiesListType[];
  };

  const handleSelectChange = (value: number) => {
    setValue('is_needed_validation', !!value);
  };

  const handleLimitChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    if (/^\d*$/.test(value)) {
      setValue('limit', value);
    }
  };

  return (
    <>
      <Controller
        control={control}
        name="commission_min"
        shouldUnregister={false}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            label="Минимальная комиссия"
            size="small"
            fullWidth
            type="number"
            InputLabelProps={{
              shrink: true,
            }}
            sx={{
              marginBottom: '24px',
            }}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
          />
        )}
      />
      <Controller
        control={control}
        name="commission_percent"
        shouldUnregister={false}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            label="Комиссия, %"
            size="small"
            fullWidth
            type="number"
            InputLabelProps={{
              shrink: true,
            }}
            sx={{
              marginBottom: '24px',
            }}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
          />
        )}
      />
      <Controller
        control={control}
        name="limit"
        shouldUnregister={false}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            label="Лимит"
            size="small"
            fullWidth
            type="text"
            InputLabelProps={{
              shrink: true,
            }}
            sx={{
              marginBottom: '24px',
            }}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            onChange={handleLimitChange}
          />
        )}
      />
      <Controller
        control={control}
        name="city_ids"
        render={({ field }) => (
          <Autocomplete
            {...field}
            multiple
            sx={{
              marginBottom: '24px',
            }}
            fullWidth
            limitTags={2}
            disableCloseOnSelect
            disablePortal
            options={(citiesList as CitiesListType[]) || []}
            getOptionLabel={(option) => option.name_ru}
            value={selectValues as CitiesListType[]}
            filterOptions={filterOptions}
            onChange={handleAutocompleteChange}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  style={{ marginRight: 8 }}
                  checked={option.name_ru === 'Выбрать все' ? !!(selectValues.length === citiesList.length) : selected}
                />
                {option.name_ru}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Список городов"
              />
            )}
          />
        )}
      />
      <Controller
        control={control}
        name="is_needed_validation"
        render={({ field: { value, onChange, ...restField } }) => (
          <FormControl fullWidth>
            <InputLabel id="select-label">Подтверждение при выводе средств</InputLabel>
            <Select
              labelId="select-label"
              label="Подтверждение при выводе средств"
              value={Number(value)}
              onChange={(event) => handleSelectChange(event.target.value as number)}
              sx={{
                marginBottom: '24px',
              }}
              {...restField}
            >
              <MenuItem value={1}>Да</MenuItem>
              <MenuItem value={0}>Нет</MenuItem>
            </Select>
          </FormControl>
        )}
      />
      <Button
        variant="contained"
        onClick={handleSubmit(onSubmit)}
      >
        Сохранить
      </Button>
    </>
  );
};
